import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import type { Event } from '@/types';
import { EventList } from './EventList';
import { formatDateOnlyMonthDay } from '@/lib/date';

// 格式化单个事件的日期
function formatEventDate(e: Event): string {
  if (e.event_type === 'specific' && e.start_date) {
    const start = formatDateOnlyMonthDay(e.start_date);
    if (!start) {
      return '';
    }
    if (!e.end_date) {
      return start;
    }
    const end = formatDateOnlyMonthDay(e.end_date);
    if (!end) {
      return start;
    }
    return `${start}-${end}`;
  }
  if (!e.month || !e.day) {
    return '';
  }
  return formatDateOnlyMonthDay(`2000-${String(e.month).padStart(2, '0')}-${String(e.day).padStart(2, '0')}`);
}

// 获取事件列表的代表性日期（用于标题显示）
function getRepresentativeDate(events: Event[], formatCount: (date: string, count: number) => string): string {
  if (!events.length) {
    return '';
  }

  // 收集所有事件的日期
  const dates = events.map(e => formatEventDate(e)).filter(Boolean);
  if (!dates.length) {
    return '';
  }

  // 如果所有事件日期相同，返回该日期
  const uniqueDates = [...new Set(dates)];
  if (uniqueDates.length === 1) {
    return uniqueDates[0];
  }

  // 对于多日事件，如果日期不同，显示第一个事件的日期和数量
  return formatCount(dates[0], events.length);
}

interface EventsSectionProps {
  events: Event[];
  isToday: boolean;
}

/* Section 01: today's events, or the next ones coming up. */
export function EventsSection({ events, isToday }: EventsSectionProps) {
  const { t } = useTranslation('public');
  if (!events.length) {
    return null;
  }
  // 今天的事件日期就是页眉的日期，只有“即将到来”才需要说明是哪一天。
  let dateStr = getRepresentativeDate(events, (date, count) => t('events.multipleDates', { date, count }));
  let heading = t('events.upcoming');
  if (isToday) {
    heading = t('events.today');
    dateStr = '';
  }
  return (
    <section aria-labelledby="events-heading">
      <div className="section-head">
        <h2 id="events-heading" className="m-0">{heading}</h2>
        {dateStr && <span className="kicker">{dateStr}</span>}
      </div>
      <EventList events={events} showYear={true} />
    </section>
  );
}

interface PastEventsSectionProps {
  events: Event[];
}

/* Section 02: recently passed events; collapsed behind a text link on phones. */
export function PastEventsSection({ events }: PastEventsSectionProps) {
  const { t } = useTranslation('public');
  const [isExpanded, setIsExpanded] = useState(false);

  if (!events.length) {
    return null;
  }

  // 只显示最近一个事件的日期，不显示范围
  const recentDate = formatEventDate(events[0]);
  let toggleLabel = t('expand');
  let listClassName = 'hidden lg:block';
  if (isExpanded) {
    toggleLabel = t('collapse');
    listClassName = 'block';
  }

  return (
    <section aria-labelledby="past-events-heading">
      <div className="section-head">
        <h2 id="past-events-heading" className="m-0">{t('events.past')}</h2>
        {recentDate && <span className="kicker">{recentDate}</span>}
        {/* 移动端显示折叠按钮，桌面端隐藏 */}
        <button
          type="button"
          className="text-link ml-auto lg:hidden"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="past-events-list"
        >
          {toggleLabel}
        </button>
      </div>
      {/* 移动端默认折叠，桌面端始终展开 */}
      <div id="past-events-list" className={listClassName}>
        <EventList events={events} showYear={true} />
      </div>
    </section>
  );
}
